import React, { useState, useMemo } from 'react';
import { X, Sparkles, Loader2, Download, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'krankenhaus', label: 'Krankenhaus' },
  { value: 'stadtverwaltung', label: 'Stadtverwaltung' },
  { value: 'versicherung', label: 'Versicherung' },
  { value: 'bank_sparkasse', label: 'Bank / Sparkasse' },
  { value: 'stadtwerke_energie', label: 'Stadtwerke / Energie' },
  { value: 'versandhandel', label: 'Versandhandel' },
  { value: 'krankenkasse', label: 'Krankenkasse' },
  { value: 'verlag_medien', label: 'Verlag / Medien' },
  { value: 'kanzlei', label: 'Kanzlei' },
  { value: 'industrie', label: 'Industrieunternehmen' },
  { value: 'lettershop_druckerei', label: 'Lettershop / Druckerei' },
  { value: 'it_telekom', label: 'IT / Telekommunikation' },
  { value: 'einzelhandel', label: 'Einzelhandel' },
  { value: 'sonstige', label: 'Sonstige' },
];

const BrancheBriefingModal = ({ password, onClose }) => {
  const [type, setType] = useState('krankenhaus');
  const [keywords, setKeywords] = useState('');
  const [generating, setGenerating] = useState(false);
  const [briefing, setBriefing] = useState(null);

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const generate = async () => {
    setGenerating(true);
    setBriefing(null);
    try {
      const res = await fetch(`${API}/admin/outreach/branche-briefing`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, keywords: keywords.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Briefing fehlgeschlagen');
      }
      const data = await res.json();
      setBriefing(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    if (!briefing?.content) return;
    const blob = new Blob([briefing.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Branchen-Briefing-${type}-${briefing.created_at.slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="branche-briefing-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between border-b border-slate-200"
          style={{ background: 'linear-gradient(90deg, rgba(255,162,102,0.15), rgba(255,162,102,0.05))' }}
        >
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen size={18} className="text-orange-600" />
            Branchen-Briefing
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/60">
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Marktüberblick, Pain Points, Entscheider-Profile, Aufhänger und Einwände
            für eine ganze Branche — ideal vor einem Akquise-Block.
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1 block">
                Branche
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
                data-testid="branche-briefing-type"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1 block">
                Zusätzliche Stichworte (optional)
              </label>
              <Textarea
                placeholder="z. B. Fokus auf Süddeutschland; aktuell viele KHZG-Investitionen; Schwerpunkt mittelgroße Häuser"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="min-h-[60px]"
                data-testid="branche-briefing-keywords"
              />
            </div>
            <Button
              onClick={generate}
              disabled={generating}
              className="text-white"
              style={{ backgroundColor: '#FFA266' }}
              data-testid="branche-briefing-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Wird erstellt (20–35 Sek.) …
                </>
              ) : (
                <>
                  <Sparkles size={14} className="mr-2" />
                  Branchen-Briefing erstellen
                </>
              )}
            </Button>
          </div>

          {briefing && (
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Briefing
                </span>
                <Button variant="outline" size="sm" onClick={download}>
                  <Download size={13} className="mr-1.5" />
                  Als .md herunterladen
                </Button>
              </div>
              <div className="markdown-briefing border border-slate-200 rounded-md p-4 max-h-[500px] overflow-y-auto" data-testid="branche-briefing-content">
                <ReactMarkdown>{briefing.content}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrancheBriefingModal;
